import {
  AnalyticsApplyPayload,
  AnalyticsInsuranceMethod,
  AnalyticsRejectError,
  AnalyticsRejectPayload,
  InsuranceAcceptEventData,
  InsuranceEventErrorType,
  InsuranceOpenEventData,
  InsuranceRejectEventData,
} from '@typesApp/Analytics'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { SimplifiedBenefitPlanItem } from '@typesApp/InsurancePlan'
import { ProductType } from '@typesApp/Product'
import { titleCase } from './camelCase'

type EventBundle = {
  insurer?: string
  insuranceAmount?: string
  benefits?: string
  method?: AnalyticsInsuranceMethod
}

const USER_ERRORS: AnalyticsRejectError[] = ['LIMIT_REACHED', 'INSUFFICIENT_COVERAGE', 'INSURANCE_MEMBER_NOT_FOUND']

type BenefitType = 'frames' | 'lenses' | 'contact'

const BENEFITS_MAP = {
  COLORED_PLANO_CONTACT_LENS: 'contact',
  PRESCRIPTION_CONTACT_LENS: 'contact',
  FRAME: 'frames',
  SUNGLASSES: 'frames',
  PRESCRIPTION_SUNGLASSES: 'frames',
  PRESCRIPTION_LENS: 'lenses',
  PROGRESSIVE_LENS: 'lenses',
  SINGLE_VISION_LENS: 'lenses',
  BIFOCAL_LENS: 'lenses',
  PHOTOCHROMATIC_TINT: 'lenses',
  TINTING: 'lenses',
  ANTI_REFLECTIVE_TREATMENT: 'lenses',
  SOLUTIONS: 'contact',
} as Record<ProductType, BenefitType>

type EventParams = {
  insurerName?: string
  claimRecord?: ClaimRecord
  benefitPlans?: SimplifiedBenefitPlanItem[]
  isMemberIdForm?: boolean
}

type AnalyticsUtilReturn = {
  createOpenEventData: () => InsuranceOpenEventData
  createApplyEventData: (payload: AnalyticsApplyPayload) => InsuranceAcceptEventData
  createRejectEventData: (payload: AnalyticsRejectPayload) => InsuranceRejectEventData
}

const analyticsUtil = (): AnalyticsUtilReturn => {
  const mapEventBundle = (options: EventParams): EventBundle => {
    const { insurerName, claimRecord, benefitPlans, isMemberIdForm } = options

    const insurer = titleCase(insurerName ?? claimRecord?.claimData?.primaryCoverage?.insurer?.name ?? '')
    const amount =
      claimRecord?.claimData?.claim?.confirmedAmount?.amount ??
      claimRecord?.claimData?.quote?.quoteConfirmedAmount?.amount
    const insuranceAmount = amount !== undefined ? Number(-amount).toFixed(2) : ''
    const method: AnalyticsInsuranceMethod = isMemberIdForm ? 'memberid' : 'standard'
    const benefits = Array.isArray(benefitPlans)
      ? getBenefitsFromPlans(benefitPlans)
      : getBenefitsFromClaimRecord(claimRecord)

    return {
      insurer,
      insuranceAmount,
      benefits,
      method,
    }
  }

  const getBenefitsFromPlans = (benefitPlans: SimplifiedBenefitPlanItem[]): string => {
    return benefitPlans
      .filter(benefit => benefit.benefitStatus === 'BENEFITS_AVAILABLE')
      .map(plan => {
        switch (plan?.planType) {
          case 'FRAME':
            return 'frames'
          case 'CONTACT_LENSES':
            return 'contact'
          default:
            return plan?.planType?.replace('_', ' ') ?? ''
        }
      })
      ?.join(',')
      ?.toLowerCase()
  }

  const getBenefitsFromClaimRecord = (claimRecord?: ClaimRecord): string => {
    const products = claimRecord?.eligibleProducts ?? []
    const productTypes = products.flatMap(product =>
      product.cartProducts?.filter(p => p.insuranceEligible)?.flatMap(p => p.claimProductTypes ?? [])
    )
    const uniqueProductTypes = Array.from(new Set(productTypes))
    return uniqueProductTypes
      .map(type => BENEFITS_MAP[type] ?? '')
      .filter(type => type)
      .join(',')
  }

  const createOpenEventData = (): InsuranceOpenEventData => {
    return {
      id: 'Impression',
      Page_Section2: 'InsurancePanel',
    }
  }

  const createApplyEventData = (payload: AnalyticsApplyPayload): InsuranceAcceptEventData => {
    const bundle = mapEventBundle(payload)
    return {
      id: 'Event',
      Events_InsuranceApplied: '1',
      Order_InsuranceCode: bundle?.insurer ?? '',
      Order_InsuranceAmount: bundle?.insuranceAmount ?? '',
      Order_InsuranceMethod: bundle?.method ?? 'standard',
      Order_InsuranceBenefits: bundle.benefits ?? '',
    }
  }

  const getErrorSource = (error?: AnalyticsRejectError): InsuranceEventErrorType => {
    if (error) {
      return USER_ERRORS.includes(error) ? 'User' : 'Server'
    }
    return 'Server'
  }

  const createRejectEventData = (payload: AnalyticsRejectPayload): InsuranceRejectEventData => {
    const { insurerName, claimRecord, error, isMemberIdForm } = payload
    const bundle = mapEventBundle({ insurerName, claimRecord, isMemberIdForm })
    return {
      id: 'Event',
      Events_InsuranceTentative: '1',
      Order_InsuranceCode: bundle.insurer ?? '',
      Order_InsuranceMethod: bundle.method ?? 'standard',
      Error_Source: getErrorSource(error),
      Error_Code: 'Insurance',
      // Error_Message may be further adjusted prior to sending event
      Error_Message: error,
      error,
    }
  }

  return {
    createOpenEventData,
    createApplyEventData,
    createRejectEventData,
  }
}

export const AnalyticsUtil = analyticsUtil()
